import { useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Upload, FileSpreadsheet, X } from "lucide-react";

const FIELDS = [
  { key: "customer", label: "Client (name or email)", required: true },
  { key: "scheduled_date", label: "Date", required: true },
  { key: "scheduled_time", label: "Time" },
  { key: "package", label: "Package / service" },
  { key: "price", label: "Price" },
  { key: "duration_hours", label: "Duration (hrs)" },
  { key: "address", label: "Address" },
  { key: "notes", label: "Notes" },
  { key: "status", label: "Status" },
];

export default function ImportJobsDialog({ open, onOpenChange, onImported }) {
  const fileInput = useRef(null);
  const [fileName, setFileName] = useState("");
  const [headers, setHeaders] = useState([]);
  const [rows, setRows] = useState([]);
  const [mapping, setMapping] = useState({});
  const [matchBy, setMatchBy] = useState("either");
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);

  const reset = () => {
    setFileName(""); setHeaders([]); setRows([]); setMapping({}); setResult(null);
    if (fileInput.current) fileInput.current.value = "";
  };

  const handleClose = (v) => {
    if (!v) reset();
    onOpenChange(v);
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/jobs/import/preview", fd);
      setHeaders(data.headers);
      setRows(data.rows);
      setMapping(data.suggested || {});
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not read the CSV file");
      reset();
    } finally {
      setLoading(false);
    }
  };

  const setField = (key) => (e) => setMapping({ ...mapping, [key]: e.target.value });

  const validCount = mapping.customer && mapping.scheduled_date
    ? rows.filter((r) => (r[mapping.customer] || "").trim() && (r[mapping.scheduled_date] || "").trim()).length
    : 0;

  const doImport = async () => {
    if (!mapping.customer) return toast.error("Choose which column identifies the client");
    if (!mapping.scheduled_date) return toast.error("Choose which column holds the booking date");
    const jobs = rows
      .map((r) => ({
        customer_ref: (r[mapping.customer] || "").trim(),
        scheduled_date: (r[mapping.scheduled_date] || "").trim(),
        scheduled_time: mapping.scheduled_time ? (r[mapping.scheduled_time] || "").trim() : "",
        package_ref: mapping.package ? (r[mapping.package] || "").trim() : "",
        price: mapping.price ? parseFloat((r[mapping.price] || "0").replace(/[^0-9.]/g, "")) || 0 : 0,
        duration_hours: mapping.duration_hours ? parseFloat((r[mapping.duration_hours] || "0").replace(/[^0-9.]/g, "")) || 0 : 0,
        address: mapping.address ? (r[mapping.address] || "").trim() : "",
        notes: mapping.notes ? (r[mapping.notes] || "").trim() : "",
        status: mapping.status ? (r[mapping.status] || "scheduled").trim() : "scheduled",
      }))
      .filter((j) => j.customer_ref && j.scheduled_date);
    if (jobs.length === 0) return toast.error("No rows with a client and date were found");
    setImporting(true);
    try {
      const { data } = await api.post("/jobs/import", { jobs, match_by: matchBy });
      setResult(data);
      toast.success(`${data.imported} booking${data.imported === 1 ? "" : "s"} imported`);
      onImported?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Import failed");
    } finally {
      setImporting(false);
    }
  };

  const previewRows = rows.slice(0, 4);

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="rounded-sm max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Import Bookings from CSV</DialogTitle>
        </DialogHeader>

        {result ? (
          <div className="py-4 space-y-3" data-testid="jobs-import-result">
            <p className="text-sm"><span className="font-medium text-brand">{result.imported}</span> bookings imported.</p>
            {result.skipped_no_customer?.length > 0 && (
              <div className="text-sm">
                <div className="font-medium">Skipped — client not found ({result.skipped_no_customer.length}):</div>
                <div className="text-muted-foreground mt-1 max-h-24 overflow-y-auto">
                  {result.skipped_no_customer.join(", ")}
                </div>
                <p className="text-xs text-muted-foreground mt-1">Add these clients first (or import them), then re-run.</p>
              </div>
            )}
            {result.skipped_bad_date?.length > 0 && (
              <div className="text-sm">
                <div className="font-medium">Skipped — unreadable date ({result.skipped_bad_date.length}):</div>
                <div className="text-muted-foreground mt-1">{result.skipped_bad_date.join(", ")}</div>
              </div>
            )}
          </div>
        ) : headers.length === 0 ? (
          <div className="py-6">
            <button
              type="button"
              onClick={() => fileInput.current?.click()}
              data-testid="jobs-import-dropzone"
              className="w-full border border-dashed border-brand/50 rounded-sm py-12 flex flex-col items-center gap-3 text-muted-foreground hover:border-brand hover:bg-accent/40 transition-colors"
            >
              <Upload className="w-8 h-8" strokeWidth={1.5} />
              <div className="text-sm">{loading ? "Reading file…" : "Click to choose a CSV file"}</div>
              <div className="text-xs">Export your bookings from Maidily as CSV, then upload it here.</div>
            </button>
            <input
              ref={fileInput}
              type="file"
              accept=".csv,text/csv"
              onChange={onFile}
              className="hidden"
              data-testid="jobs-import-file-input"
            />
            <p className="text-xs text-muted-foreground mt-4">
              Bookings are matched to existing clients. Any booking whose client isn't in your list is skipped and reported.
            </p>
          </div>
        ) : (
          <div className="space-y-6 py-2 max-h-[65vh] overflow-y-auto pr-1">
            <div className="flex items-center gap-2 text-sm">
              <FileSpreadsheet className="w-4 h-4 text-brand" />
              <span className="font-medium">{fileName}</span>
              <span className="text-muted-foreground">· {rows.length} rows found</span>
              <button onClick={reset} className="ml-auto text-muted-foreground hover:text-destructive" data-testid="jobs-import-clear">
                <X className="w-4 h-4" />
              </button>
            </div>

            <div>
              <Label className="label-caps text-muted-foreground">Match your columns</Label>
              <div className="grid grid-cols-2 gap-4 mt-3">
                {FIELDS.map((f) => (
                  <div key={f.key} className="space-y-1.5">
                    <Label className="text-sm">
                      {f.label}{f.required && <span className="text-brand"> *</span>}
                    </Label>
                    <select
                      value={mapping[f.key] || ""}
                      onChange={setField(f.key)}
                      data-testid={`job-map-${f.key}`}
                      className="w-full border border-border/70 rounded-sm px-3 py-2 bg-background text-sm outline-none focus:border-brand"
                    >
                      <option value="">— Skip —</option>
                      {headers.map((h) => (
                        <option key={h} value={h}>{h}</option>
                      ))}
                    </select>
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-1.5 max-w-xs">
              <Label className="text-sm">Match clients by</Label>
              <select
                value={matchBy}
                onChange={(e) => setMatchBy(e.target.value)}
                data-testid="job-match-by"
                className="w-full border border-border/70 rounded-sm px-3 py-2 bg-background text-sm outline-none focus:border-brand"
              >
                <option value="either">Email or name</option>
                <option value="email">Email only</option>
                <option value="name">Name only</option>
              </select>
            </div>

            {mapping.customer && mapping.scheduled_date && previewRows.length > 0 && (
              <div>
                <Label className="label-caps text-muted-foreground">Preview</Label>
                <div className="mt-3 border border-border/60 rounded-sm overflow-hidden text-sm">
                  <div className="grid grid-cols-3 bg-accent/40 label-caps text-xs px-3 py-2">
                    <div>Client</div><div>Date</div><div>Service</div>
                  </div>
                  {previewRows.map((r, i) => (
                    <div key={i} className="grid grid-cols-3 px-3 py-2 border-t border-border/50" data-testid={`jobs-import-preview-row-${i}`}>
                      <div className="truncate pr-2">{r[mapping.customer] || "—"}</div>
                      <div className="truncate pr-2">{r[mapping.scheduled_date] || "—"}{mapping.scheduled_time ? ` ${r[mapping.scheduled_time] || ""}` : ""}</div>
                      <div className="truncate">{mapping.package ? (r[mapping.package] || "—") : "—"}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => handleClose(false)} className="rounded-sm">
            {result ? "Close" : "Cancel"}
          </Button>
          {headers.length > 0 && !result && (
            <Button onClick={doImport} disabled={importing || !mapping.customer || !mapping.scheduled_date} data-testid="confirm-jobs-import-button" className="rounded-sm">
              {importing ? "Importing…" : `Import ${validCount} Booking${validCount === 1 ? "" : "s"}`}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
